/**
 * DADDL bundle popup — delay, frequency cap, CTA to bundle builder.
 * Shell open/close is <daddl-overlay> (assets/daddl-overlay.js).
 * This custom element only owns the pausable countdown and dismiss storage.
 */
(function () {
  'use strict';

  if (customElements.get('daddl-bundle-popup')) return;

  const STORAGE_KEY = 'daddl_bundle_popup_v1';
  const TICK_MS = 500;

  /* True while something else owns the screen. Pass the host so our own
   * overlay is not treated as a blocker after it opens. */
  function somethingElseOpen(host) {
    if (document.body.classList.contains('overflow-hidden')) return true;
    if (document.body.style.overflow === 'hidden') return true;
    if (document.documentElement.style.overflow === 'hidden') return true;
    if (document.querySelector('cart-drawer.active, cart-drawer.animate')) return true;
    if (document.querySelector('details[open] > .menu-drawer, .menu-opening')) return true;
    if (document.querySelector('daddl-bundle-builder.is-open')) return true;
    const openOverlay = document.querySelector('daddl-overlay[open]');
    if (openOverlay && openOverlay !== host?.overlay) return true;
    return false;
  }

  class DaddlBundlePopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.overlay = this.querySelector('daddl-overlay');

      if (this.overlay) {
        this.overlay.addEventListener('daddl-overlay:close', () => {
          if (this._silentClose) {
            this._silentClose = false;
            return;
          }
          this.recordDismiss();
        });
      }

      const cta = this.querySelector('[data-popup-cta]');
      if (cta) {
        cta.addEventListener('click', () => {
          this.dismiss();
          const builder = document.querySelector('daddl-bundle-builder');
          if (builder && typeof builder.open === 'function') {
            builder.open(cta);
          } else if (this.dataset.fallbackUrl) {
            window.location.href = this.dataset.fallbackUrl;
          }
        });
      }

      /* Theme editor: only show while the section is selected. */
      if (window.Shopify && Shopify.designMode) {
        const sectionEl = this.closest('.shopify-section');
        if (sectionEl) {
          sectionEl.addEventListener('shopify:section:select', () => this.show());
          sectionEl.addEventListener('shopify:section:deselect', () => this.hide());
        }
        return;
      }

      if (!this.shouldShow()) return;

      /* Pausable countdown: accumulate visible-and-unblocked time only,
       * starting once the page has fully loaded. */
      this.delayMs = (parseInt(this.dataset.delay, 10) || 15) * 1000;
      this.elapsedMs = 0;
      const startTicking = () => {
        this.ticker = window.setInterval(() => this.tick(), TICK_MS);
      };
      if (document.readyState === 'complete') {
        startTicking();
      } else {
        window.addEventListener('load', startTicking, { once: true });
      }
    }

    disconnectedCallback() {
      if (this.ticker) window.clearInterval(this.ticker);
    }

    tick() {
      if (document.visibilityState !== 'visible') return;
      if (somethingElseOpen(this)) return;

      this.elapsedMs += TICK_MS;
      if (this.elapsedMs < this.delayMs) return;

      window.clearInterval(this.ticker);
      this.ticker = null;
      this.show();
    }

    shouldShow() {
      try {
        const state = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (!state.timestamp) return true;
        const days = parseInt(this.dataset.dismissDays, 10) || 3;
        return (Date.now() - state.timestamp) / 86400000 > days;
      } catch (err) {
        return true;
      }
    }

    show() {
      if (this.overlay && typeof this.overlay.open === 'function') {
        this.overlay.open();
      }
    }

    /* Hide without recording a dismissal (theme editor deselect). */
    hide() {
      if (!this.overlay || !this.overlay.hasAttribute('open')) return;
      this._silentClose = true;
      this.overlay.close();
    }

    recordDismiss() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now() }));
      } catch (err) {
        /* noop */
      }
    }

    dismiss() {
      this.recordDismiss();
      this.hide();
    }
  }

  customElements.define('daddl-bundle-popup', DaddlBundlePopup);
})();
